// Projeto: Haklai Church — Plugin WordPress Modular - Desenvolvido por: Ricardo Sarmento - https://linx.pt

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';

function CellsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Estados de filtros
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  const [selectedCells, setSelectedCells] = useState([]);

  // Debounce para busca
  const [searchDebounced, setSearchDebounced] = useState('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Carrega células com filtros
  const { data: cellsData, isLoading, error, refetch } = useQuery({
    queryKey: ['cells', searchDebounced, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchDebounced) params.append('search', searchDebounced);
      if (statusFilter === 'active') params.append('is_active', '1');
      if (statusFilter === 'inactive') params.append('is_active', '0');

      const response = await api.get(`/cells?${params.toString()}`);
      return response.data.data || [];
    },
  });

  const cells = cellsData || [];

  // Filtra células localmente (caso o backend não filtre)
  const filteredCells = cells.filter(cell => {
    if (statusFilter === 'active' && !cell.is_active) return false;
    if (statusFilter === 'inactive' && cell.is_active) return false;
    if (searchDebounced) {
      const searchLower = searchDebounced.toLowerCase();
      return (
        cell.name?.toLowerCase().includes(searchLower) ||
        cell.description?.toLowerCase().includes(searchLower) ||
        cell.address?.toLowerCase().includes(searchLower) ||
        cell.neighborhood?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  // Mutation para deletar célula
  const deleteMutation = useMutation({
    mutationFn: async (cellId) => {
      return api.delete(`/cells/${cellId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cells'] });
      setSelectedCells([]);
    },
    onError: (error) => {
      alert('Erro ao deletar célula: ' + (error.response?.data?.message || error.message));
    },
  });

  // Handler de deletar
  const handleDelete = (cellId, e) => {
    e.stopPropagation();
    if (window.confirm('Tem certeza que deseja deletar esta célula? Esta ação não pode ser desfeita.')) {
      deleteMutation.mutate(cellId);
    }
  };

  // Handler de deletar múltiplas
  const handleBulkDelete = () => {
    if (selectedCells.length === 0) {
      alert('Selecione pelo menos uma célula');
      return;
    }
    if (window.confirm(`Tem certeza que deseja deletar ${selectedCells.length} célula(s)? Esta ação não pode ser desfeita.`)) {
      selectedCells.forEach(cellId => {
        deleteMutation.mutate(cellId);
      });
    }
  };

  // Toggle seleção de célula
  const toggleCellSelection = (cellId, e) => {
    e.stopPropagation();
    setSelectedCells(prev =>
      prev.includes(cellId)
        ? prev.filter(id => id !== cellId)
        : [...prev, cellId]
    );
  };


  // Formata dia da semana
  const getDayOfWeekLabel = (day) => {
    const days = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    return day !== null && day >= 0 && day <= 6 ? days[day] : '-';
  };

  // Formata hora
  const formatTime = (time) => {
    if (!time) return '-';
    // Se já está no formato HH:MM, retorna
    if (time.match(/^\d{2}:\d{2}$/)) return time;
    // Tenta converter outros formatos
    return time;
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Erro ao carregar células</h2>
          <p>{error.message || 'Erro desconhecido'}</p>
          <button className="btn btn-primary" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="cells-list-page" style={{ padding: '2rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>Células</h1>
            <p style={{ color: 'var(--text-secondary)' }}>
              {filteredCells.length} {filteredCells.length === 1 ? 'célula encontrada' : 'células encontradas'}
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/cells/new')}
          >
            <span className="material-symbols-outlined" style={{ marginRight: '0.5rem' }}>add</span>
            Adicionar Célula
          </button>
        </div>

        {/* Filtros */}
        <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {/* Busca */}
            <div className="form-group">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                className="form-input"
                placeholder="Nome, descrição, endereço..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Filtro de Status */}
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">Todas</option>
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
              </select>
            </div>
          </div>

          {/* Ações em massa */}
          {selectedCells.length > 0 && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                {selectedCells.length} {selectedCells.length === 1 ? 'célula selecionada' : 'células selecionadas'}
              </span>
              <button
                className="btn btn-danger"
                onClick={handleBulkDelete}
                disabled={deleteMutation.isPending}
              >
                Deletar Selecionadas
              </button>
              <button
                className="btn btn-text"
                onClick={() => setSelectedCells([])}
              >
                Limpar Seleção
              </button>
            </div>
          )}
        </div>

        {/* Lista de Células */}
        {filteredCells.length === 0 ? (
          <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '3rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              groups
            </span>
            <h3 style={{ marginBottom: '0.5rem' }}>Nenhuma célula encontrada</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
              {searchDebounced || statusFilter !== 'all'
                ? 'Tente ajustar os filtros de busca'
                : 'Comece adicionando sua primeira célula'}
            </p>
            {!searchDebounced && statusFilter === 'all' && (
              <button
                className="btn btn-primary"
                onClick={() => navigate('/cells/new')}
              >
                Adicionar Primeira Célula
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {filteredCells.map(cell => (
              <div
                key={cell.id}
                className="card"
                style={{
                  padding: '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => navigate(`/cells/${cell.id}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                }}
              >
                {/* Header do Card */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                      {cell.name}
                    </h3>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                      <span className={`badge ${cell.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {cell.is_active ? 'Ativa' : 'Inativa'}
                      </span>
                      {cell.current_members !== undefined && (
                        <span className="badge badge-primary">
                          {cell.current_members} {cell.current_members === 1 ? 'membro' : 'membros'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedCells.includes(cell.id)}
                      onChange={(e) => toggleCellSelection(cell.id, e)}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>

                {/* Descrição */}
                {cell.description && (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1rem', lineHeight: '1.5' }}>
                    {cell.description.length > 100 ? cell.description.substring(0, 100) + '...' : cell.description}
                  </p>
                )}

                {/* Informações */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                  {cell.leader && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--primary)', fontWeight: '600' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>person</span>
                      <span>Líder: {cell.leader.full_name}</span>
                    </div>
                  )}
                  {cell.address && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>location_on</span>
                      <span>{cell.address}</span>
                    </div>
                  )}
                  {cell.neighborhood && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>place</span>
                      <span>{cell.neighborhood}</span>
                    </div>
                  )}
                  {(cell.day_of_week !== null || cell.start_time) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>schedule</span>
                      <span>
                        {getDayOfWeekLabel(cell.day_of_week)} {cell.start_time && `às ${formatTime(cell.start_time)}`}
                      </span>
                    </div>
                  )}
                  {cell.max_capacity && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>people</span>
                      <span>Capacidade: {cell.max_capacity} pessoas</span>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '1rem' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn btn-text btn-sm"
                    onClick={() => navigate(`/cells/${cell.id}`)}
                    title="Ver detalhes"
                  >
                    <span className="material-symbols-outlined">visibility</span>
                    Ver
                  </button>
                  <button
                    className="btn btn-text btn-sm"
                    onClick={() => navigate(`/cells/${cell.id}/edit`)}
                    title="Editar"
                  >
                    <span className="material-symbols-outlined">edit</span>
                    Editar
                  </button>
                  <button
                    className="btn btn-text btn-sm btn-danger"
                    onClick={(e) => handleDelete(cell.id, e)}
                    title="Deletar"
                    disabled={deleteMutation.isPending}
                  >
                    <span className="material-symbols-outlined">delete</span>
                    Deletar
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default CellsListPage;

